/**
 * Team Profile page — public, anonymous.
 *
 * Shows team info, upcoming fixtures, recent results, and the team's top
 * performers this season (goals + assists). Each fixture row taps through
 * to the Match Detail page; tapping the star here favorites the team.
 *
 * Backend: GET /api/live-scores/team/:id (6h cached)
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useScoresNavigate } from './scoresPath';
import { ArrowLeft, Star, Loader2, Calendar, History, Trophy, MapPin, Users, Bell, BellRing, BarChart3, ChevronDown, X } from 'lucide-react';
import { toast } from 'sonner';
import { useFavoriteTeams } from './useScoresFavorites';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_LIVE = ['1H', '2H', 'ET', 'P', 'HT'];

const ScoresTeamProfile = () => {
  const { id } = useParams();
  const navigate = useScoresNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playerId, setPlayerId] = useState(null);
  const favTeams = useFavoriteTeams();
  const teamIdNum = Number(id);
  const isFav = favTeams.isFavorite(teamIdNum);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API_URL}/api/live-scores/team/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive) { setData(j); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const toggleFav = () => {
    if (!data?.team) return;
    favTeams.toggle({ id: data.team.id, name: data.team.name, logo: data.team.logo });
    toast.success(isFav ? 'Removed from favorites' : 'Added to favorites');
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>;
  }
  if (!data || !data.team) {
    return <div className="text-center text-sm text-zinc-500 py-20">Team profile unavailable.</div>;
  }

  const { team, upcoming = [], recent = [], top_players: topPlayers = [], squad = [], form = [], team_stats: teamStats = null } = data;
  const formDisplay = [...form].reverse(); // oldest → newest (most recent on the right)

  return (
    <div className="pb-4" data-testid="scores-team-profile">
      {/* Header */}
      <div className="sticky top-14 z-30 bg-[var(--sk-bg)]/95 backdrop-blur-xl border-b border-[var(--sk-border)] px-3 py-2.5 flex items-center gap-3">
        <button onClick={() => { if (typeof window !== 'undefined' && window.history.length > 1) navigate(-1); else navigate('/'); }} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center" aria-label="Back" data-testid="team-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 text-[12px] text-zinc-400 truncate">Team Profile</div>
        <button
          onClick={toggleFav}
          className={`w-9 h-9 rounded-full flex items-center justify-center ${isFav ? 'bg-accent-soft text-accent' : 'bg-white/5 text-zinc-400'}`}
          data-testid="team-fav-toggle"
        >
          <Star className={`w-4 h-4 ${isFav ? 'fill-current' : ''}`} />
        </button>
      </div>

      {/* Hero */}
      <div className="px-4 pt-5 pb-3 flex items-center gap-4">
        {team.logo && <img src={team.logo} alt="" className="w-16 h-16 object-contain" />}
        <div className="flex-1 min-w-0">
          <div className="text-xl font-black text-white leading-tight">{team.name}</div>
          <div className="text-[12px] text-zinc-500 flex items-center gap-1 mt-0.5">
            {team.country}{team.founded ? ` · Est. ${team.founded}` : ''}
          </div>
          {team.venue_name && (
            <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3" />
              {team.venue_name}{team.venue_capacity ? ` · ${team.venue_capacity.toLocaleString()} cap` : ''}
            </div>
          )}
          {formDisplay.length > 0 && (
            <div className="flex items-center gap-1.5 mt-2" data-testid="team-form">
              <span className="text-[10px] uppercase tracking-wider text-zinc-500 mr-0.5">Form</span>
              {formDisplay.map((r, i) => (
                <span key={i} className={`w-5 h-5 rounded text-[10px] font-black flex items-center justify-center ${
                  r === 'W' ? 'bg-emerald-500/20 text-emerald-300'
                    : r === 'L' ? 'bg-rose-500/20 text-rose-300'
                    : 'bg-zinc-500/20 text-zinc-300'
                }`}>{r}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="px-3 space-y-4">
        {/* Season stats */}
        {teamStats && teamStats.played != null && (
          <Section icon={BarChart3} label={`Season Stats${teamStats.league_name ? ` · ${teamStats.league_name}` : ''}`} testid="team-season-stats">
            <div className="grid grid-cols-3 gap-2">
              <StatBox label="Played" value={teamStats.played} />
              <StatBox label="Won" value={teamStats.wins} accent="emerald" />
              <StatBox label="Drawn" value={teamStats.draws} />
              <StatBox label="Lost" value={teamStats.losses} accent="rose" />
              <StatBox label="Goals For" value={teamStats.goals_for} accent="emerald" />
              <StatBox label="Goals Against" value={teamStats.goals_against} accent="rose" />
              <StatBox label="Clean Sheets" value={teamStats.clean_sheets} />
              <StatBox label="Failed to Score" value={teamStats.failed_to_score} />
              {teamStats.goals_for != null && teamStats.goals_against != null && (
                <StatBox label="Goal Diff" value={(teamStats.goals_for - teamStats.goals_against) > 0 ? `+${teamStats.goals_for - teamStats.goals_against}` : (teamStats.goals_for - teamStats.goals_against)} />
              )}
            </div>
          </Section>
        )}

        {/* Squad */}
        {squad.length > 0 && (
          <SquadSection squad={squad} onPlayerClick={setPlayerId} />
        )}

        {/* Top performers */}
        {topPlayers.length > 0 && (
          <Section icon={Trophy} label="Top Performers · This Season" testid="team-top-players">
            <div className="rounded-xl bg-[var(--sk-surface)] overflow-hidden">
              {topPlayers.map((p, i) => (
                <button key={p.player_id || i} onClick={() => p.player_id && setPlayerId(p.player_id)} className="w-full flex items-center gap-2.5 px-3 py-2.5 border-b border-[var(--sk-border)]/60 last:border-0 text-left hover:bg-white/[0.02]" data-testid={`top-player-${p.player_id}`}>
                  <span className="w-4 text-center text-[11px] font-bold text-zinc-500">{i + 1}</span>
                  {p.photo ? (
                    <img src={p.photo} alt="" className="w-8 h-8 rounded-full object-cover bg-zinc-800" loading="lazy" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-zinc-800" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-white truncate">{p.name}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{p.position || ''}{p.appearances ? ` · ${p.appearances} apps` : ''}</div>
                  </div>
                  <div className="flex items-center gap-3 text-right">
                    {p.goals > 0 && <Pill label="G" value={p.goals} accent="emerald" />}
                    {p.assists > 0 && <Pill label="A" value={p.assists} accent="blue" />}
                  </div>
                </button>
              ))}
            </div>
          </Section>
        )}

        {/* Upcoming fixtures */}
        <Section icon={Calendar} label={`Upcoming · ${upcoming.length}`} testid="team-upcoming">
          {upcoming.length === 0 ? (
            <EmptyText>No upcoming fixtures scheduled.</EmptyText>
          ) : (
            <div className="rounded-xl bg-[var(--sk-surface)] overflow-hidden">
              {upcoming.map((m) => (
                <FixtureRow
                  key={m.fixture_id}
                  match={m}
                  teamId={teamIdNum}
                  onClick={() => navigate(`/match/${m.fixture_id}`)}
                />
              ))}
            </div>
          )}
        </Section>

        {/* Recent results */}
        <Section icon={History} label="Recent Results" testid="team-recent">
          {recent.length === 0 ? (
            <EmptyText>No recent results.</EmptyText>
          ) : (
            <div className="rounded-xl bg-[var(--sk-surface)] overflow-hidden">
              {recent.map((m) => (
                <FixtureRow
                  key={m.fixture_id}
                  match={m}
                  teamId={teamIdNum}
                  onClick={() => navigate(`/match/${m.fixture_id}`)}
                  isResult
                />
              ))}
            </div>
          )}
        </Section>
      </div>

      <PlayerModal playerId={playerId} onClose={() => setPlayerId(null)} />
    </div>
  );
};

const Section = ({ icon: Icon, label, children, testid }) => (
  <section data-testid={testid}>
    <div className="flex items-center gap-1.5 mb-2 px-1">
      <Icon className="w-3.5 h-3.5 text-accent" />
      <h2 className="text-[10px] font-black uppercase tracking-wider text-zinc-400">{label}</h2>
    </div>
    {children}
  </section>
);

const EmptyText = ({ children }) => (
  <div className="rounded-xl bg-[var(--sk-surface)] px-3 py-4 text-[11px] text-zinc-500 text-center">{children}</div>
);

const PlayerModal = ({ playerId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!playerId) { setData(null); return; }
    let alive = true;
    setLoading(true);
    setData(null);
    fetch(`${API_URL}/api/live-scores/player/${playerId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive) { setData(j); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [playerId]);

  if (!playerId) return null;
  const p = data?.player;
  const s = data?.stats || {};

  return (
    <div className="fixed inset-0 z-[220] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="player-modal">
      <div className="w-full sm:max-w-sm bg-[var(--sk-bg)] border-t sm:border border-[var(--sk-border)] rounded-t-3xl sm:rounded-3xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="relative p-5 bg-gradient-to-br from-accent/15 to-transparent">
          <button onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center" aria-label="Close" data-testid="player-modal-close">
            <X className="w-4 h-4 text-white" />
          </button>
          {loading || !data ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>
          ) : !p ? (
            <div className="py-8 text-center text-sm text-zinc-500">Player stats unavailable.</div>
          ) : (
            <>
              <div className="flex items-center gap-3">
                {p.photo ? <img src={p.photo} alt="" className="w-16 h-16 rounded-full object-cover bg-zinc-800 border-2 border-white/10" /> : <div className="w-16 h-16 rounded-full bg-zinc-800" />}
                <div className="min-w-0">
                  <div className="text-lg font-black text-white truncate">{p.name}</div>
                  <div className="text-[11px] text-zinc-400 flex items-center gap-1.5 flex-wrap">
                    {p.position && <span>{p.position}</span>}
                    {p.age && <span>· {p.age} yrs</span>}
                    {p.nationality && <span>· {p.nationality}</span>}
                  </div>
                  {p.team_name && (
                    <div className="text-[11px] text-zinc-500 flex items-center gap-1 mt-0.5">
                      {p.team_logo && <img src={p.team_logo} alt="" className="w-3.5 h-3.5 object-contain" />}
                      {p.team_name}
                    </div>
                  )}
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-4 mb-2">Season Stats</div>
              <div className="grid grid-cols-4 gap-2">
                <StatBox label="Apps" value={s.appearances} />
                <StatBox label="Goals" value={s.goals} accent="emerald" />
                <StatBox label="Assists" value={s.assists} accent="blue" />
                <StatBox label="Rating" value={s.rating ?? '–'} />
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <StatBox label="Minutes" value={s.minutes} />
                <StatBox label="Yellow" value={s.yellow} />
                <StatBox label="Red" value={s.red} accent="rose" />
              </div>
              {(data.by_competition || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">By Competition</div>
                  <div className="rounded-xl bg-[var(--sk-surface)] overflow-hidden">
                    {data.by_competition.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2 border-b border-[var(--sk-border)]/60 last:border-0">
                        {c.league_logo && <img src={c.league_logo} alt="" className="w-4 h-4 object-contain shrink-0" />}
                        <span className="text-[11px] text-white truncate flex-1">{c.league}</span>
                        <span className="text-[10px] text-zinc-400 tabular-nums shrink-0">{c.appearances} apps · {c.goals}G · {c.assists}A</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};


const StatBox = ({ label, value, accent }) => {
  const color = accent === 'emerald' ? 'text-emerald-300' : accent === 'rose' ? 'text-rose-300' : 'text-white';
  return (
    <div className="rounded-xl bg-[var(--sk-surface)] px-3 py-2.5 text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`text-lg font-black tabular-nums ${color}`}>{value != null ? value : '–'}</div>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 mt-0.5">{label}</div>
    </div>
  );
};

const POSITION_ORDER = ['Goalkeeper', 'Defender', 'Midfielder', 'Attacker'];

const SquadSection = ({ squad, onPlayerClick }) => {
  const [open, setOpen] = useState(false);
  const grouped = POSITION_ORDER.map((pos) => ({
    pos,
    players: squad.filter((p) => p.position === pos),
  })).filter((g) => g.players.length > 0);
  const other = squad.filter((p) => !POSITION_ORDER.includes(p.position));
  if (other.length) grouped.push({ pos: 'Other', players: other });

  return (
    <Section icon={Users} label={`Squad · ${squad.length}`} testid="team-squad">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl bg-[var(--sk-surface)] mb-2"
        data-testid="team-squad-toggle"
      >
        <span className="text-[12px] text-white font-semibold">{open ? 'Hide full squad' : 'View full squad'}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-3">
          {grouped.map((g) => (
            <div key={g.pos}>
              <div className="text-[10px] font-black uppercase tracking-wider text-accent mb-1.5 px-1">{g.pos}s</div>
              <div className="rounded-xl bg-[var(--sk-surface)] overflow-hidden">
                {g.players.map((p, i) => (
                  <button key={p.player_id || i} onClick={() => p.player_id && onPlayerClick?.(p.player_id)} className="w-full flex items-center gap-2.5 px-3 py-2 border-b border-[var(--sk-border)]/60 last:border-0 text-left hover:bg-white/[0.02]" data-testid={`squad-player-${p.player_id}`}>
                    {p.photo ? (
                      <img src={p.photo} alt="" className="w-8 h-8 rounded-full object-cover bg-zinc-800" loading="lazy" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-zinc-800" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-white truncate">{p.name}</div>
                      <div className="text-[10px] text-zinc-500">{p.position}{p.age ? ` · ${p.age} yrs` : ''}</div>
                    </div>
                    {p.number != null && (
                      <span className="text-[12px] font-black text-zinc-500 tabular-nums w-6 text-right">{p.number}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
};

const Pill = ({ label, value, accent }) => {
  const cls = accent === 'emerald'
    ? 'bg-emerald-500/15 text-emerald-300'
    : 'bg-blue-500/15 text-blue-300';
  return (
    <div className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${cls}`}>
      <span className="font-black mr-0.5">{value}</span>{label}
    </div>
  );
};

const FixtureRow = ({ match: m, teamId, onClick, isResult }) => {
  const isHome = m.home_id === teamId;
  const opponent = isHome
    ? { id: m.away_id, name: m.away_team, logo: m.away_logo }
    : { id: m.home_id, name: m.home_team, logo: m.home_logo };
  const isLive = STATUS_LIVE.includes(m.status_short);

  let resultBadge = null;
  if (isResult && m.home_goals != null && m.away_goals != null) {
    const our = isHome ? m.home_goals : m.away_goals;
    const their = isHome ? m.away_goals : m.home_goals;
    const result = our > their ? 'W' : our < their ? 'L' : 'D';
    const cls = result === 'W' ? 'bg-emerald-500/20 text-emerald-300'
      : result === 'L' ? 'bg-rose-500/20 text-rose-300'
      : 'bg-zinc-500/20 text-zinc-300';
    resultBadge = <span className={`w-5 h-5 rounded text-[10px] font-black flex items-center justify-center ${cls}`}>{result}</span>;
  }

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 border-b border-[var(--sk-border)]/60 last:border-0 hover:bg-white/[0.02] text-left"
      data-testid={`team-fixture-${m.fixture_id}`}
    >
      <div className="w-10 shrink-0 text-center">
        {isLive ? (
          <div className="flex flex-col items-center"><span className="live-dot" /><span className="text-[10px] text-[var(--sk-live)] font-bold mt-0.5">{m.elapsed ? `${m.elapsed}'` : 'LIVE'}</span></div>
        ) : m.date ? (
          <div>
            <div className="text-[10px] text-zinc-400 font-bold">{new Date(m.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>
            <div className="text-[9px] text-zinc-500">{new Date(m.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        ) : (
          <span className="text-[10px] text-zinc-500">TBD</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-zinc-500 shrink-0">{isHome ? 'vs' : '@'}</span>
          {opponent.logo && <img src={opponent.logo} alt="" className="w-4 h-4 object-contain" loading="lazy" />}
          <span className="text-[12px] text-white truncate">{opponent.name}</span>
        </div>
        <div className="flex items-center gap-1 truncate mt-0.5">
          {m.league_logo && <img src={m.league_logo} alt="" className="w-3 h-3 object-contain shrink-0" loading="lazy" />}
          <span className="text-[10px] text-zinc-500 truncate">{m.league_name}</span>
        </div>
      </div>
      {isResult && m.home_goals != null && (
        <div className="text-[13px] font-black text-white tabular-nums shrink-0">
          {isHome ? `${m.home_goals}-${m.away_goals}` : `${m.away_goals}-${m.home_goals}`}
        </div>
      )}
      {resultBadge}
    </button>
  );
};

export default ScoresTeamProfile;
